/**
 * Amenities Component
 *
 * Grid display of property amenities with icons.
 */

import React, { useState } from 'react';

// Amenity data with icons
const AMENITY_DATA: Record<string, { label: string; icon: React.ReactNode; category: string }> = {
  pool: { label: 'Pool', icon: <PoolIcon />, category: 'Features' },
  wifi: { label: 'WiFi', icon: <WifiIcon />, category: 'Essentials' },
  kitchen: { label: 'Kitchen', icon: <KitchenIcon />, category: 'Essentials' },
  ac: { label: 'Air conditioning', icon: <AcIcon />, category: 'Essentials' },
  parking: { label: 'Free parking', icon: <ParkingIcon />, category: 'Features' },
  washer: { label: 'Washer', icon: <WasherIcon />, category: 'Essentials' },
  dryer: { label: 'Dryer', icon: <DryerIcon />, category: 'Essentials' },
  fireplace: { label: 'Fireplace', icon: <FireplaceIcon />, category: 'Features' },
  bbq: { label: 'BBQ grill', icon: <BbqIcon />, category: 'Features' },
  garden: { label: 'Garden', icon: <GardenIcon />, category: 'Features' },
  beach_access: { label: 'Beach access', icon: <BeachIcon />, category: 'Features' },
  gym: { label: 'Gym', icon: <GymIcon />, category: 'Features' },
  hot_tub: { label: 'Hot tub', icon: <HotTubIcon />, category: 'Features' },
  tv: { label: 'TV', icon: <TvIcon />, category: 'Essentials' },
  heating: { label: 'Heating', icon: <HeatingIcon />, category: 'Essentials' },
  smoke_detector: { label: 'Smoke detector', icon: <SmokeDetectorIcon />, category: 'Safety' },
  fire_extinguisher: { label: 'Fire extinguisher', icon: <FireExtinguisherIcon />, category: 'Safety' },
  first_aid: { label: 'First aid kit', icon: <FirstAidIcon />, category: 'Safety' },
};

interface AmenitiesProps {
  amenityIds: string[];
}

export const Amenities: React.FC<AmenitiesProps> = ({ amenityIds }) => {
  const [showAll, setShowAll] = useState(false);

  const amenities = amenityIds
    .map((id) => ({ id, ...AMENITY_DATA[id] }))
    .filter((a) => a.label);

  const displayAmenities = showAll ? amenities : amenities.slice(0, 8);

  return (
    <section className="py-6 border-b border-gray-200">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">
        What this place offers
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {displayAmenities.map((amenity) => (
          <div key={amenity.id} className="flex items-center gap-3">
            <span className="w-6 h-6 text-gray-600">{amenity.icon}</span>
            <span className="text-gray-700">{amenity.label}</span>
          </div>
        ))}
      </div>

      {amenities.length > 8 && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="mt-6 px-6 py-3 border border-gray-900 rounded-lg font-medium hover:bg-gray-50 transition-colors"
        >
          {showAll
            ? 'Show less'
            : `Show all ${amenities.length} amenities`}
        </button>
      )}
    </section>
  );
};

// Amenity Icons
function PoolIcon() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
    </svg>
  );
}

function WifiIcon() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.14 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
    </svg>
  );
}

function KitchenIcon() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
    </svg>
  );
}

function AcIcon() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );
}

function ParkingIcon() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V5a2 2 0 00-2-2H5z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17V7h4a3 3 0 010 6H9" />
    </svg>
  );
}

function WasherIcon() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4h16v16H4V4z" />
      <circle cx="12" cy="13" r="4" strokeWidth={1.5} />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 8h.01M11 8h.01" />
    </svg>
  );
}

function DryerIcon() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4h16v16H4V4z" />
      <circle cx="12" cy="13" r="4" strokeWidth={1.5} />
    </svg>
  );
}

function FireplaceIcon() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
    </svg>
  );
}

function BbqIcon() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  );
}

function GardenIcon() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
  );
}

function BeachIcon() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function GymIcon() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h2v12H4V6zM18 6h2v12h-2V6zM6 11h12v2H6v-2z" />
    </svg>
  );
}

function HotTubIcon() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707" />
    </svg>
  );
}

function TvIcon() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );
}

function HeatingIcon() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
    </svg>
  );
}

function SmokeDetectorIcon() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );
}

function FireExtinguisherIcon() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
    </svg>
  );
}

function FirstAidIcon() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
    </svg>
  );
}

export default Amenities;
